import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiEdit, FiTrash2, FiGlobe, FiTrendingUp, FiFileText, FiShield, FiAlertTriangle, FiCheckCircle, FiCalendar } = FiIcons;

const DomainCard = ({ domain, onEdit, onDelete, articleCount, totalEarnings }) => {
  const getSpamScoreColor = (spamScore) => {
    if (spamScore <= 10) return 'text-success-600 bg-success-100';
    return 'text-danger-600 bg-danger-100';
  };

  const getSpamScoreIcon = (spamScore) => {
    if (spamScore <= 10) return FiCheckCircle;
    return FiAlertTriangle;
  };

  const getAuthorityColor = (authority) => {
    if (authority >= 70) return 'text-green-600'; // High authority - Green
    if (authority >= 50) return 'text-emerald-600'; // Good authority - Emerald
    if (authority >= 30) return 'text-teal-600'; // Medium authority - Teal
    return 'text-blue-600'; // Low authority - Blue
  };

  const calculateDomainAge = (registrationDate) => {
    if (!registrationDate) return null;
    
    try {
      let regDate;
      
      // Handle different date formats
      if (registrationDate instanceof Date) {
        regDate = registrationDate;
      } else if (typeof registrationDate === 'string') {
        regDate = new Date(registrationDate);
      } else if (registrationDate.toDate && typeof registrationDate.toDate === 'function') {
        // Firebase Timestamp
        regDate = registrationDate.toDate();
      } else {
        regDate = new Date(registrationDate);
      }
      
      if (isNaN(regDate.getTime())) return null;
      
      const today = new Date();
      const diffTime = Math.abs(today - regDate);
      const diffYears = diffTime / (1000 * 60 * 60 * 24 * 365.25);
      
      if (diffYears < 1) {
        const diffMonths = Math.floor(diffYears * 12);
        return `${diffMonths}m`;
      } else {
        return `${Math.floor(diffYears)}y`;
      }
    } catch (error) {
      console.warn('Error calculating domain age:', error);
      return null;
    }
  };

  const getAgeColor = (registrationDate) => {
    if (!registrationDate) return 'text-gray-500';
    
    try {
      let regDate;
      
      // Handle different date formats
      if (registrationDate instanceof Date) {
        regDate = registrationDate;
      } else if (typeof registrationDate === 'string') {
        regDate = new Date(registrationDate);
      } else if (registrationDate.toDate && typeof registrationDate.toDate === 'function') {
        // Firebase Timestamp
        regDate = registrationDate.toDate();
      } else {
        regDate = new Date(registrationDate);
      }
      
      if (isNaN(regDate.getTime())) return 'text-gray-500';
      
      const today = new Date();
      const diffYears = (today - regDate) / (1000 * 60 * 60 * 24 * 365.25);
      
      if (diffYears >= 10) return 'text-green-600'; // 10+ years - Excellent
      if (diffYears >= 5) return 'text-emerald-600'; // 5-10 years - Great
      if (diffYears >= 2) return 'text-teal-600'; // 2-5 years - Good
      if (diffYears >= 1) return 'text-blue-600'; // 1-2 years - Decent
      return 'text-orange-600'; // < 1 year - New
    } catch (error) {
      console.warn('Error getting age color:', error);
      return 'text-gray-500';
    }
  };

  const domainAge = calculateDomainAge(domain.registration_date);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-2">
            {domain.favicon_url ? (
              <img
                src={domain.favicon_url}
                alt={`${domain.name} favicon`}
                className="w-6 h-6 rounded-sm"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <SafeIcon
              icon={FiGlobe}
              className="w-5 h-5 text-primary-600"
              style={{ display: domain.favicon_url ? 'none' : 'flex' }}
            />
            <h3 className="text-lg font-semibold text-gray-900">{domain.name}</h3>
          </div>
          <p className="text-sm text-gray-600 mb-3">{domain.url}</p>

          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className="flex items-center space-x-2">
              <SafeIcon icon={FiShield} className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-500">Authority:</span>
              <span className={`text-sm font-semibold ${getAuthorityColor(domain.domain_authority || 0)}`}>
                {domain.domain_authority || 'N/A'}
              </span>
            </div>

            <div className="flex items-center space-x-2">
              <SafeIcon icon={getSpamScoreIcon(domain.spam_score || 0)} className="w-4 h-4" />
              <span className="text-sm text-gray-500">Spam:</span>
              <span className={`text-xs font-semibold px-2 py-1 rounded-full ${getSpamScoreColor(domain.spam_score || 0)}`}>
                {domain.spam_score || 0}%
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className="flex items-center space-x-2">
              <SafeIcon icon={FiCalendar} className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-500">Age:</span>
              <span className={`text-sm font-semibold ${getAgeColor(domain.registration_date)}`}>
                {domainAge || 'N/A'}
              </span>
            </div>
            <div className="text-sm text-gray-500">
              <span>Niche: {domain.niche}</span>
            </div>
          </div>
        </div>

        <div className="flex space-x-2">
          <button
            onClick={() => onEdit(domain)}
            className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
          >
            <SafeIcon icon={FiEdit} className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete(domain)}
            className="p-2 text-gray-500 hover:text-danger-600 hover:bg-danger-50 rounded-lg transition-colors"
          >
            <SafeIcon icon={FiTrash2} className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 pt-4 border-t">
        <div className="text-center">
          <div className="flex items-center justify-center space-x-1 mb-1">
            <SafeIcon icon={FiFileText} className="w-4 h-4 text-gray-500" />
            <span className="text-sm text-gray-600">Articles</span>
          </div>
          <p className="text-2xl font-bold text-primary-600">{articleCount}</p>
        </div>

        <div className="text-center">
          <div className="flex items-center justify-center space-x-1 mb-1">
            <SafeIcon icon={FiTrendingUp} className="w-4 h-4 text-gray-500" />
            <span className="text-sm text-gray-600">Earnings</span>
          </div>
          <p className="text-2xl font-bold text-success-600">{totalEarnings.toFixed(2)} LEI</p>
        </div>
      </div>

      {domain.registration_date && (
        <div className="mt-4 pt-4 border-t">
          <div className="text-sm text-gray-600">
            <span className="font-medium">Registered:</span>{' '}
            {(() => {
              try {
                let date;
                if (domain.registration_date instanceof Date) {
                  date = domain.registration_date;
                } else if (typeof domain.registration_date === 'string') {
                  date = new Date(domain.registration_date);
                } else if (domain.registration_date.toDate && typeof domain.registration_date.toDate === 'function') {
                  date = domain.registration_date.toDate();
                } else {
                  date = new Date(domain.registration_date);
                }
                
                return !isNaN(date.getTime()) ? date.toLocaleDateString() : 'Invalid Date';
              } catch (error) {
                return 'Invalid Date';
              }
            })()}
          </div>
        </div>
      )}

      {domain.notes && (
        <div className="mt-4 pt-4 border-t">
          <p className="text-sm text-gray-600">{domain.notes}</p>
        </div>
      )}
    </motion.div>
  );
};

export default DomainCard;